// Нэг хичээлийн улирлын дүн (/study/c/:courseId?s=&t=): дүн ("Улирлын дүн") → дүн гарсан суралцахуйн үр дүн, шалгуурын үнэлгээтэй →
// багшийн зураг, нэр, коммент (сурагчид өөрт нь хандсан хэлбэрээр) + онцгой коммент → асуулт-хариу, "Багшаас тодруулах".
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Input, Modal, Spin, message } from "antd";
import { FiArrowLeft, FiMessageSquare } from "react-icons/fi";
import SubjectIcon from "components/SubjectIcon";
import { instance } from "utils/axios";
import dayjs from "dayjs";
import { get, base, TermChips, Dots, MARK, tone } from "./shared";

// Асуулт-хариу (эцэг эх ↔ багш) — багшийн комментын доор
function Questions({ list }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-col gap-2.5 border-t border-[#e5d8f5] pt-3">
      {list.map((x) => (
        <div key={x.id} className="flex flex-col gap-1.5">
          <div className="self-end max-w-[90%] rounded-2xl rounded-br-md bg-white border border-[#e5d8f5] px-3 py-2 text-sm">
            <div className="text-[11px] text-gray-500 mb-0.5">{x.askedBy || "Эцэг эх"} · {dayjs(x.askedAt).format("MM/DD HH:mm")}</div>
            <div className="whitespace-pre-wrap">{x.question}</div>
          </div>
          {x.answer
            ? (
              <div className="self-start max-w-[90%] rounded-2xl rounded-bl-md bg-[#f3eeff] px-3 py-2 text-sm">
                <div className="text-[11px] text-primary font-semibold mb-0.5">{x.answeredBy ? `${x.answeredBy} багш` : "Багш"} · {dayjs(x.answeredAt).format("MM/DD HH:mm")}</div>
                <div className="whitespace-pre-wrap">{x.answer}</div>
              </div>
            )
            : <div className="self-start text-xs text-gray-400">Багшийн хариуг хүлээж байна</div>}
        </div>
      ))}
    </div>
  );
}

// Эцэг эх: хичээлийн багшид асуулт илгээх (багшийн мэдэгдэл + имэйл)
function AskTeacher({ who, d, onSent }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = () => {
    setBusy(true);
    instance({ method: "post", url: `${base(who)}/course/${d.course.id}/ask`, data: { text, termId: d.term.id }, silent: true })
      .then((r) => { message.success("Багшид илгээлээ"); setOpen(false); setText(""); if (r.data?.question) onSent(r.data.question); })
      .catch((e) => message.error(e?.response?.data?.error || "Илгээж чадсангүй"))
      .then(() => setBusy(false));
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="pchip pchip-off !h-9 self-start flex items-center gap-1.5"><FiMessageSquare /> Багшаас тодруулах</button>
      <Modal open={open} onCancel={() => setOpen(false)} title={`Багшаас тодруулах${d.teacher ? ` · ${d.teacher.name} багш` : ""}`} footer={[
        <Button key="c" onClick={() => setOpen(false)}>Болих</Button>,
        <Button key="s" type="primary" loading={busy} disabled={text.trim().length < 3} onClick={send}>Илгээх</Button>,
      ]}>
        <p className="text-sm text-gray-600 mt-0">{d.course.subject} хичээлийн талаар асуух зүйлээ бичнэ үү. Багшид мэдэгдэл, имэйлээр очих бөгөөд таны утас, имэйлээр холбогдоно.</p>
        <Input.TextArea value={text} onChange={(e) => setText(e.target.value)} autoSize={{ minRows: 4, maxRows: 10 }} maxLength={2000} placeholder="Асуултаа бичнэ үү…" />
      </Modal>
    </>
  );
}

export default function StudyCourse() {
  const { courseId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const who = params.get("s") || "me";
  const term = params.get("t") || undefined;
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    get(`${base(who)}/course/${courseId}`, term ? { termId: term } : {}).then((x) => alive && setD(x)).catch(() => alive && setD({ error: true }));
    return () => { alive = false; };
  }, [who, courseId, term]);

  const back = () => (window.history.length > 1 ? nav(-1) : nav(who === "me" ? "/study" : `/child?c=${who}`));
  if (!d) return <div className="flex justify-center py-16"><Spin /></div>;
  if (d.error) return <div className="pt-8 max-w-3xl mx-auto text-sm text-gray-500">Хичээл олдсонгүй.</div>;
  const graded = d.outcomes.filter((o) => o.level != null);
  const comment = d.published ? ((who === "me" && d.comment?.studentAuto) || d.comment?.auto || d.comment?.text) : null;
  const special = d.published && d.comment?.auto ? d.comment.special : null;

  return (
    <div className="pt-4 md:pt-8 pb-8 max-w-3xl mx-auto w-full flex flex-col gap-4">
      <button type="button" onClick={back} className="self-start text-sm text-primary flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer"><FiArrowLeft /> Сурлага</button>
      <div className="flex items-center gap-4">
        <SubjectIcon name={d.course.subject} size={56} />
        <div className="flex-1 min-w-0">
          <h1 className="m-0 text-[26px] md:text-[32px] font-extrabold text-primary leading-tight">{d.course.subject}</h1>
          <div className="text-sm text-gray-500">{d.term.name}</div>
        </div>
        {d.published && d.grade?.total != null
          ? (
            <span className="shrink-0 flex flex-col items-end">
              <span className="text-[40px] font-extrabold tabular-nums leading-none text-primary">{Math.round(d.grade.total)}%</span>
              <span className="text-xs font-semibold text-gray-500 mt-1">Улирлын дүн</span>
            </span>
          )
          : <span className="shrink-0 text-sm text-gray-400">Дүн гараагүй</span>}
      </div>
      <TermChips terms={d.terms} value={d.term.id} onChange={(t) => setParams(Object.fromEntries(Object.entries({ s: who !== "me" ? who : undefined, t }).filter(([, v]) => v)), { replace: true })} />

      {/* Дүн гарсан суралцахуйн үр дүн — шалгуур бүрийн үнэлгээтэй (судлаагүйг харуулахгүй) */}
      {graded.length > 0 && (
        <section className="tj-card p-4 flex flex-col gap-1">
          <h2 className="m-0 text-base font-bold mb-1">Суралцахуйн үр дүн</h2>
          <ul className="list-none m-0 p-0 divide-y divide-[#f0edf5]">
            {graded.map((o) => (
              <li key={o.id} className="py-3 flex flex-col gap-2">
                <div className="flex items-start gap-3">
                  <span className="flex-1 min-w-0">
                    {o.topic && <span className="block text-[11px] text-gray-500">{o.topic}</span>}
                    <span className="block text-sm font-semibold leading-snug">{o.code ? <b className="text-primary mr-1">{o.code}</b> : null}{o.text}</span>
                  </span>
                  <span className={`shrink-0 rounded-lg px-2 py-0.5 text-sm font-bold tabular-nums ${tone(o.level)}`}>{o.level.toFixed(1)}</span>
                </div>
                {o.criteria.some((c) => c.level != null || c.mark) && (
                  <div className="pl-2 flex flex-col gap-1.5">
                    {o.criteria.filter((c) => c.level != null || c.mark).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-600"><span className="flex-1">{c.text}</span>{c.level != null ? <Dots v={c.level} /> : <span className="text-gray-400">{MARK[c.mark] || c.mark}</span>}</div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="m-0 mt-1 text-xs text-gray-500">Түвшин: 1 Эхлэл · 2 Хөгжиж буй · 3 Хангасан · 4 Давсан</p>
        </section>
      )}

      {/* Багшийн зураг, нэр, коммент (зан төлөвөөс үүссэн бичвэр + онцгой коммент) */}
      {(comment || d.teacher) && d.published && (
        <section className="rounded-2xl bg-[#faf7ff] border border-[#e5d8f5] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3.5">
            {d.teacher && <img src={d.teacher.photo || d.teacher.demoPhoto} alt="" className="h-16 w-16 rounded-full object-cover bg-white border-2 border-[#e5d8f5] shrink-0" />}
            <div className="flex flex-col gap-0.5 leading-tight">
              {d.teacher && <span className="text-base font-bold text-[#2d1b4e]">{d.teacher.name} <span className="font-normal text-gray-500">багш</span></span>}
              <span className="text-xs font-bold text-primary">Багшийн зөвлөмж</span>
            </div>
          </div>
          {comment && <p className="m-0 text-sm leading-relaxed whitespace-pre-wrap">{comment}</p>}
          {special && (
            <div className="rounded-xl bg-white border border-[#e5d8f5] px-3 py-2.5">
              <div className="text-xs font-bold text-primary mb-1">Онцгойлон хэлэх зүйл</div>
              <p className="m-0 text-sm leading-relaxed whitespace-pre-wrap">{special}</p>
            </div>
          )}
          <Questions list={d.questions} />
          <AskTeacher who={who} d={d} onSent={(qn) => setD((x) => ({ ...x, questions: [...(x.questions || []), qn] }))} />}
        </section>
      )}
    </div>
  );
}
